import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { Step } from '../../../core/models';
import { CleanObjectService, CompaniesService, StorageService } from '../../../core/services';
import { VacanciesService } from '../../vacancies.service';
import { faLock, faX } from '@fortawesome/free-solid-svg-icons';

export function spaceValidator(control: FormControl) {
	if (control && control.value && !control.value.replace(/\s/g, '').length) {
		control.setValue('');
		return { required: true }
	} else {
		return null;
	}
}

@Component({
	selector: 'app-create-shortlist-event',
	templateUrl: './create-shortlist-event.component.html',
	styleUrls: ['./create-shortlist-event.component.scss']
})
export class CreateShortlistEventComponent implements OnInit {
	@Input() public steps: Step[] = [];
	@Input() public vacancyId: string = '';
	@Input() public shortlistId!: string;
	@ViewChild('externalOpportunityInputElement') public externalOpportunityInputElement!: ElementRef<HTMLInputElement>;

	private allExternalOpportunityOptions: any[] = [];
	private managerEmail: string | null = null;
	private responsibleEmail: string | null = null;
	private shortlist: any = {};

	public form!: FormGroup;
	public company: any;
	public externalOpportunityFilteredOptions: any[] = [];
	public externalOpportunitySelectedOptions: any[] = [];
	public loadedFields: boolean = false;
	public submitted: boolean = false;

	public icons: any = { 'lock': faLock, 'delete': faX };
	public emailsFromTheExternalLinks: {
		value: string;
		blocked: boolean;
	}[] = [];

	constructor(
		private formBuilder: FormBuilder,
		private activeModal: NgbActiveModal,
		private toastrService: ToastrService,
		private vacanciesService: VacanciesService,
		private clearService: CleanObjectService,
		private storage: StorageService,
		private companiesService: CompaniesService,
	) {}
	
	ngOnInit(): void {
		this.company = this.storage.getCompany();

		if (this.shortlistId) this.loadedFields = true;

		if (this.company.canUseExternalJobList) {
			this.companiesService.getExternalJobs(this.company._id, {
				filters: {
					vacancyId: this.vacancyId
				}
			})
				.subscribe((response: any[]) => {
					this.allExternalOpportunityOptions = response;
					this.externalOpportunityFilteredOptions = this.filterExternalOpportunityOptions();
				})
		}

		this.form = this.formBuilder.group({
			externalJob: new FormControl(''),
			name: [null, [Validators.compose([Validators.required, Validators.minLength(2)]), spaceValidator]],
			stepId: [this.steps && this.steps.length === 1 ? this.steps[0]._id : null, Validators.required],
			responsible: [null, [Validators.required, spaceValidator]],
			responsibleEmail: [null, [Validators.compose([Validators.required, Validators.email]), spaceValidator]],
			manager: [null, Validators.required],
			managerEmail: [null, [Validators.compose([Validators.required, Validators.email]), spaceValidator]],
			shippingDate: [null, Validators.required],
			emailsFromTheExternalLinks: [[]],
		});

		(this.form.get('externalJob') as AbstractControl).valueChanges
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				map(value => this.filterExternalOpportunityOptions(value))
			)
			.subscribe(filtered => {
				this.externalOpportunityFilteredOptions = filtered;
			});

		(this.form.get('managerEmail') as AbstractControl).valueChanges
			.subscribe(value => {
				this.managerEmail = this.synchronizeGuestEmailsToShortlist(this.managerEmail, value);
			});

		(this.form.get('responsibleEmail') as AbstractControl).valueChanges
			.subscribe(value => {
				this.responsibleEmail = this.synchronizeGuestEmailsToShortlist(this.responsibleEmail, value);
			});

		if (this.shortlistId) {
			this.vacanciesService.getVacancyShortlistById(this.vacancyId, this.shortlistId)
				.subscribe(response => {
					this.shortlist = response;
					this.externalOpportunitySelectedOptions.push(...(this.shortlist.externalOpportunities || []))
					this.fillForm(this.shortlist);
					this.loadedFields = false;
				});
		}
	}

	//#region External opportunities
	private filterExternalOpportunityOptions(value: string = ''): any[] {
		let filterValue: string = "";

		if (typeof value === "string") {
			filterValue = value.toLowerCase();
		}

		const selectedItens: string[] = this.externalOpportunitySelectedOptions.map(element => String(element._id));

		return this.allExternalOpportunityOptions.filter((option: any) =>
			!selectedItens.includes(String(option._id)) &&
			(
				String(option.externalJobName || '').toLowerCase().includes(filterValue) ||
				String(option.externalJobId || '').toLowerCase().includes(filterValue) ||
				String(option.externalCompanyId || '').toLowerCase().includes(filterValue)
			)
		);
	}

	public externalOpportunityOptionSelected(option: any): void {
		this.externalOpportunitySelectedOptions.push(option);
		this.externalOpportunityInputElement.nativeElement.blur();

		const control = this.form.get('externalJob');
		if (control) control.reset();
	}

	public removeExternalOpportunity(index: number): void {
		this.externalOpportunitySelectedOptions.splice(index, 1);

		const control = this.form.get('externalJob');
		if (control) control.reset();
	}

	public displayExternalOpportunityWith(option: any): string {
		return option ? `${option.externalJobName}` : '';
	}
	//#endregion

	//#region 
	public addEmailToExternalLink = (term: string) => {
		const email = term.trim();
		const emailsFromTheExternalLinks: any[] = this.form.controls.emailsFromTheExternalLinks.value || [];

		if (emailsFromTheExternalLinks.map((element: any) => element.value).includes(email)) return null;

		if (this.isValidEmail(email)) {
			return {
				value: email,
				blocked: false
			};
		}

		return null;
	};

	private isValidEmail(email: string): boolean {
		return /\S+@\S+\.\S+/.test(email);
	}

	private synchronizeGuestEmailsToShortlist(previousEmail: string | null, value: string | null): string | null {
	  	const emailsControl = this.form.get('emailsFromTheExternalLinks');
		let currentEmails: any[] = emailsControl ? emailsControl.value : [];

		if (previousEmail) {
			const index: number = currentEmails.findIndex(currentEmail => currentEmail.value === previousEmail && currentEmail.blocked);
			currentEmails = currentEmails.filter((_, i) => i !== index);
		}

		if (value && this.isValidEmail(value)) {
			currentEmails.push({
				value,
				blocked: true
			});
		}

		if (emailsControl) emailsControl.setValue(currentEmails);

		return value;
	}
	//#endregion

	public close(): void {
		this.activeModal.close();
	}

	public async addEvent(): Promise<void> {
		if (!this.form.valid) {
			this.toastrService.clear();
			this.toastrService.error('Erro no formulário!', 'Preencha os campos obrigatórios!');
			return;
		}

		let shortlist = this.clearService.getCleanedAttribute({
			...this.form.value,
			externalOpportunities: this.externalOpportunitySelectedOptions
		});

		if (this.shortlistId) {
			this.vacanciesService.patchVacancyShortlist(this.vacancyId, this.shortlistId, shortlist)
				.subscribe((response: any) => {
					this.toastrService.clear();
					this.toastrService.success('A shortlist foi atualizado com sucesso!', 'Atualização realizada!');
					this.activeModal.close({
						_id: this.shortlistId,
						vacancy: this.vacancyId,
						userManager: this.shortlist.userManager,
						...shortlist,
					});
				});

			return;
		}

		this.vacanciesService.postVacancyShortlist(this.vacancyId, shortlist)
			.subscribe((response: any) => {
				this.toastrService.clear();
				this.toastrService.success('A shortlist foi cadastrada com sucesso!', 'Cadastro realizado!');
				this.activeModal.close(response);
			});
	}

	public showInvalidFeedback(fieldPath: string): boolean {
		const control: any = this.form.get(fieldPath);
		return control.invalid && (control.touched || control.dirty || this.submitted);
	}

	private fillForm(shortlist: any): void {
		this.form.patchValue(shortlist);

		this.managerEmail = (this.form.get('managerEmail') as AbstractControl).value;
		this.responsibleEmail = (this.form.get('responsibleEmail') as AbstractControl).value;

		this.synchronizeGuestEmailsToShortlist(this.managerEmail, this.managerEmail);
		this.synchronizeGuestEmailsToShortlist(this.responsibleEmail, this.responsibleEmail);
	}
}
